import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useSWR from 'swr';
import { Stack, Center, Container, useToast, Heading, HStack } from '@chakra-ui/react';
import baseService from '../../../../services/base';
import { SuccessModal } from '../../../../components/Modals';
import { QuestionnaireHeaderNew } from '../../../../components/Form';
import { InputText } from '../../../../components/Input';
import { fetchData } from '../../../../services/fetch';
import { simpleRegisterSchema } from '../../../../core/validations/simple_register';
import TemplateSelectEdit from './TemplateSelectEdit';

export default function FormEditSimple({ defaultValues, replicable, mutate, surveyId }) {
 const toast = useToast();
 const navigate = useNavigate();
 const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
 const [isReplicable, setIsReplicable] = useState(replicable);

 const { data: templates, isLoading: templatesIsLoading } = useSWR(
  `/survey-template/all`,
  fetchData
 );

 const {
  handleSubmit,
  register,
  control,
  formState: { errors, isValid, isDirty, isSubmitting },
 } = useForm({
  defaultValues,
  resolver: yupResolver(simpleRegisterSchema),
  mode: 'onChange',
 });

 const onReplicableChange = (value) => {
  setIsReplicable(value);
 };

 const onSubmit = async (values) => {
  const { name, questions } = values;
  const questionsToSend = questions?.map((q) => q?.id_question_template) || [];

  // Identificar perguntas que foram removidas
  const removedQuestions = defaultValues.questions.filter(
   (defaultQuestion) => !questionsToSend.includes(defaultQuestion.id_question_template)
  );

  // Identificar novas perguntas que não estavam em defaultValues
  const newQuestions = questionsToSend.filter(
   (questionId) =>
    !defaultValues.questions.some((q) => q.id_question_template === questionId)
  );

  const isNameChanged = name !== defaultValues.name;
  const isReplicableChanged = isReplicable !== replicable;

  try {
   // Excluir as perguntas removidas
   const deleteRequests = removedQuestions.map((question) =>
    baseService.delete(`/survey`, {
     data: { surveyId: +surveyId, questionId: question.id_question_template },
    })
   );

   if (deleteRequests.length > 0) {
    await Promise.all(deleteRequests);
   }

   // Preparar o payload para o PATCH
   const payload = {
    description: name,
    surveyQuestions: newQuestions.length > 0 ? newQuestions : [],
    type: 'simple',
   };

   // Adicionar informações de replicabilidade no payload, se alterado
   if (isReplicableChanged) {
    if (isReplicable === 'non-replicable') {
     payload.is_replicable = false;
     payload.replica_type = null

    } else {
     payload.replica_type = isReplicable;
     payload.is_replicable = true;
    }
   }
   // Verificar se há mudanças no name, novas perguntas ou replicabilidade para enviar
   if (isNameChanged || newQuestions.length > 0 || isReplicableChanged) {
    await baseService.patch(`/survey/${surveyId}`, payload);
   }

   setIsSubmitSuccessful(true);
  } catch (error) {
   showErrorToast('Não foi possível atualizar o questionário');
  }
 };

 const showErrorToast = (message) => {
  toast({
   title: 'Erro',
   description: message,
   status: 'error',
  });
 };

 const handleClose = () => {
  setIsSubmitSuccessful(false);
  mutate();
  navigate('/researches');
 };

 return (
  <form onSubmit={handleSubmit(onSubmit)}>
   <QuestionnaireHeaderNew
    heading='Editar Questionário'
    isSubmitting={isSubmitting}
    isSubmit={true}
    isSubmitEnabled={(isDirty || isReplicable !== replicable) && isValid}
    hasReplicableSelect={replicable}
    onReplicableChange={onReplicableChange}
   >
    <InputText
     {...register('name')}
     error={errors.name}
     placeholder='Digite o nome do questionário'
     w='500px'
    />
   </QuestionnaireHeaderNew>

   <Center my={['8px', '24px']} px={{ xl: '86px', '2xl': 'unset' }}>
    <Container maxW={'1280px'} p={0} minH='80vh'>
     <HStack
      bg='white'
      justify='space-between'
      p='24px'
      borderRadius='12px 12px 0 0'
      borderBottom='2px solid #F5F5FB'
     >
      <Heading as='h3' fontSize='20px' fontWeight={600}>
       Templates
      </Heading>
     </HStack>

     <Stack bg='white' spacing='0px' p={6} borderRadius='0 0 12px 12px'>
      <TemplateSelectEdit
       control={control}
       templates={templates}
       templatesIsLoading={templatesIsLoading}
      />
     </Stack>

     <SuccessModal
      isOpen={isSubmitSuccessful}
      onClose={handleClose}
      title='Questionário atualizado com sucesso!'
     />
    </Container>
   </Center>
  </form>
 );
}
